
import React, { useState, useEffect, useCallback } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Activities from './components/Activities';
import Transport from './components/Transport';
import Gallery from './components/Gallery';
import TravelTips from './components/TravelTips';
import WeatherWidget from './components/WeatherWidget';
import MapExplorer from './components/MapExplorer';
import Reviews from './components/Reviews';
import Registration from './components/Registration';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ChatAssistant from './components/ChatAssistant';
import AdminDashboard from './components/AdminDashboard';
import DesignInspector from './components/DesignInspector';
import AdminLogin from './components/AdminLogin';
import { INITIAL_CONTENT } from './constants';
import { SiteContent, View, DynamicStyle } from './types';
import { Save, Lock, Loader2, LayoutDashboard, ArrowLeft } from 'lucide-react';
import { dbService } from './services/dbService';
import { useWindowSize } from './hooks/useWindowSize';

const App: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(sessionStorage.getItem('balut_admin_authorized') === 'true');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { width } = useWindowSize();
  const isDesktop = width >= 768;
  const [activeEditId, setActiveEditId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState<SiteContent>(INITIAL_CONTENT);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentView, setCurrentView] = useState<View>('home');

  useEffect(() => {
    const init = async () => {
      try {
        const data = await dbService.fetchContent();
        if (data) {
          setContent(prev => ({ 
            ...prev, 
            ...data,
            dynamicStyles: data.dynamicStyles || {},
          }));
        }
      } catch (err) {
        console.warn('Database fetch bypassed, using local content.');
      } finally {
        setIsLoading(false);
      }
    };
    init();
    
    const timer = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const updateContent = (newContent: Partial<SiteContent>) => {
    setContent(prev => ({ ...prev, ...newContent }));
    setHasChanges(true);
  };

  const updateStyle = (id: string, style: DynamicStyle) => {
    setContent(prev => ({
      ...prev,
      dynamicStyles: { ...prev.dynamicStyles, [id]: style }
    }));
    setHasChanges(true);
  };

  const resetStyle = (id: string) => {
    setContent(prev => {
      const newStyles = { ...prev.dynamicStyles };
      delete newStyles[id];
      return { ...prev, dynamicStyles: newStyles };
    });
    setHasChanges(true);
  };

  const saveToStorage = async () => {
    setIsSaving(true);
    try {
      const result = await dbService.saveContent(content);
      if (result.success) setHasChanges(false);
    } catch (err) {
      alert('Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthorized(true);
    setIsAdmin(true);
    setIsLoggingIn(false);
    setCurrentView('admin-panel');
    sessionStorage.setItem('balut_admin_authorized', 'true');
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setIsAuthorized(false);
    sessionStorage.removeItem('balut_admin_authorized');
    navigateTo('home');
  };

  const navigateTo = useCallback((view: View | string) => {
    const sectionIds = ['discover', 'activities', 'gallery', 'guide', 'tips', 'reviews', 'contact', 'home'];
    
    if (view === 'register' || view === 'admin-panel') {
      if (view === 'admin-panel' && !isAuthorized) {
        setIsLoggingIn(true);
      } else {
        setCurrentView(view as View);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (sectionIds.includes(view)) {
      if (currentView !== 'home') setCurrentView('home');
      setTimeout(() => {
        const element = document.getElementById(view);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } else {
      setCurrentView('home');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentView, isAuthorized]);

  if (isLoading) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-white" aria-busy="true">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin mb-4" />
        <p className="text-slate-400 font-bold uppercase tracking-luxury text-[9px]">Initializing Island Portal...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-white">
      {isLoggingIn && (
        <AdminLogin 
          onSuccess={handleLoginSuccess}
          onCancel={() => {
            setIsLoggingIn(false);
            if (currentView === 'admin-panel') navigateTo('home');
          }}
        />
      )}

      {currentView !== 'admin-panel' && (
        <Navbar 
          isEditable={isAdmin && isDesktop} 
          currentView={currentView}
          onNavigate={navigateTo}
          siteName={content.siteName}
          logoUrl={content.logoUrl}
          onUpdateSiteName={(name) => updateContent({ siteName: name })}
          onUpdateLogo={(url) => updateContent({ logoUrl: url })}
          navLinks={content.navLinks}
          onUpdateNavLinks={(navLinks) => updateContent({ navLinks })}
        />
      )}
      
      <main id="main-content" className="w-full" role="main">
        {currentView === 'home' ? (
          <div className="flex flex-col">
            <Hero data={content.hero} isEditable={isAdmin && isDesktop} onUpdate={(hero) => updateContent({ hero })} onNavigate={navigateTo} styles={content.dynamicStyles} onSelect={setActiveEditId} activeId={activeEditId} />
            <About data={content.about} isAdmin={isAdmin && isDesktop} onUpdate={(about) => updateContent({ about })} styles={content.dynamicStyles} onSelect={setActiveEditId} activeId={activeEditId} />
            <Activities data={content.activities} isAdmin={isAdmin && isDesktop} onUpdate={(activities) => updateContent({ activities })} styles={content.dynamicStyles} onSelect={setActiveEditId} activeId={activeEditId} />
            <Gallery data={content.gallery} isEditable={isAdmin && isDesktop} onUpdate={(gallery) => updateContent({ gallery })} />
            <Transport data={content.transport} isAdmin={isAdmin && isDesktop} onUpdate={(transport) => updateContent({ transport })} />
            <section id="weather" className="pt-16 pb-20 bg-slate-50 border-y border-slate-100" aria-label="Current Weather Condition"><div className="container"><WeatherWidget /></div></section>
            <MapExplorer />
            <TravelTips data={content.tips} isAdmin={isAdmin && isDesktop} onUpdate={(tips) => updateContent({ tips })} />
            <Reviews />
            <Contact data={content.contact} isAdmin={isAdmin && isDesktop} onUpdate={(contact) => updateContent({ contact })} />
          </div>
        ) : currentView === 'register' ? (
          <div className="pt-32 min-h-screen bg-slate-50">
            <div className="container mb-12">
               <button onClick={() => navigateTo('home')} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-luxury text-slate-400 hover:text-sky-500 transition-colors group">
                 <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back to Discovery
               </button>
            </div>
            <Registration data={content.registration} isAdmin={isAdmin && isDesktop} onUpdate={(registration) => updateContent({ registration })} />
          </div>
        ) : currentView === 'admin-panel' ? (
          <AdminDashboard onClose={() => navigateTo('home')} currentContent={content} />
        ) : null}
      </main>

      {currentView !== 'admin-panel' && (
        <Footer 
          isAdmin={isAdmin}
          onToggleAdmin={() => isAdmin ? handleLogout() : setIsLoggingIn(true)} 
          data={content.footer}
          onUpdate={(footer) => updateContent({ footer })}
          onNavigate={navigateTo}
          siteName={content.siteName}
        />
      )}
      
      {currentView !== 'admin-panel' && <ChatAssistant />}

      {isAdmin && isDesktop && activeEditId && currentView !== 'admin-panel' && (
        <DesignInspector 
          elementId={activeEditId}
          style={content.dynamicStyles[activeEditId] || {}}
          onUpdateStyle={updateStyle}
          onReset={resetStyle}
          onClose={() => setActiveEditId(null)}
        />
      )}

      {isAdmin && (
        <div className="fixed bottom-24 right-6 z-[70] flex flex-col gap-4 items-end">
          {hasChanges && isDesktop && (
            <button 
              onClick={saveToStorage} 
              disabled={isSaving} 
              className="flex items-center gap-2.5 px-8 py-3.5 bg-white border-2 border-slate-950 text-slate-950 rounded-full font-bold shadow-2xl transition-all scale-100 hover:scale-105 active:scale-95"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} 
              <span className="text-[10px] uppercase tracking-luxury">Sync Changes</span>
            </button>
          )}
          <button 
            onClick={() => navigateTo('admin-panel')} 
            className={`flex items-center gap-2.5 px-8 py-3.5 rounded-full font-bold shadow-2xl transition-all scale-100 hover:scale-105 active:scale-95 ring-4 ring-white ${currentView === 'admin-panel' ? 'bg-slate-950 text-white' : 'bg-white text-slate-950 border border-slate-100'}`}
          >
            <LayoutDashboard className="w-4 h-4" /> 
            <span className="text-[10px] uppercase tracking-luxury">Command Center</span>
          </button>
          <button 
            onClick={handleLogout} 
            className="flex items-center gap-2.5 bg-slate-950 text-white px-8 py-3.5 rounded-full font-bold shadow-2xl transition-all scale-100 hover:scale-105 active:scale-95 ring-4 ring-white"
          >
            <Lock className="w-3.5 h-3.5" /> 
            <span className="text-[10px] uppercase tracking-luxury">Exit Staff Mode</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default App;
